// src/components/DistrictManagers.jsx
import React from 'react';
import styled from 'styled-components';
import { normalizeMediaUrl } from '../api/base';

const ManagersWrap = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  
  @media (max-width: 980px) {
    flex-direction: row;
    flex-wrap: wrap;
    justify-content: center;
    gap: 20px;
    
    /* В мобильной версии показываем только 1 менеджера в ряд */
    .manager-card {
      flex: 1;
      min-width: 280px;
      max-width: 320px;
    }
  }
  
  @media (max-width: 640px) {
    flex-direction: column;
    gap: 16px;
    
    .manager-card {
      max-width: 100%;
    }
  }
`;

const ManagerCard = styled.div`
  border: 1px solid var(--bdr);
  border-radius: 16px;
  background: var(--card);
  box-shadow: var(--shadow);
  padding: 20px;
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  transition: all 0.3s ease;
  
  &:hover {
    transform: translateY(-3px);
    box-shadow: 0 12px 32px rgba(0, 0, 0, 0.15);
    border-color: var(--primary);
  }
`;

const Avatar = styled.div`
  width: 80px;
  height: 80px;
  border-radius: 50%;
  overflow: hidden;
  background: linear-gradient(135deg, #f5f5f5, #e8e8e8);
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 800;
  font-size: 24px;
  color: var(--muted);
  margin-bottom: 16px;
  border: 3px solid var(--primary);
  
  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
`;

const ManagerInfo = styled.div`
  margin-bottom: 16px;
  
  .name {
    font-weight: 700;
    font-size: 17px;
    margin-bottom: 6px;
    line-height: 1.3;
    color: var(--fg);
  }
  
  .position {
    font-size: 14px;
    color: var(--muted);
    margin-bottom: 8px;
    font-weight: 500;
  }
  
  .contact {
    font-size: 13px;
    color: var(--muted);
    line-height: 1.4;
    
    div {
      margin-bottom: 2px;
    }
  }
`;

const Actions = styled.div`
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
  justify-content: center;
  width: 100%;
  
  .btn {
    padding: 10px 16px;
    font-size: 13px;
    border-radius: 8px;
    text-decoration: none;
    transition: all 0.2s ease;
    font-weight: 600;
    flex: 1;
    min-width: 100px;
    text-align: center;
  }
  
  .btn-primary {
    background: var(--primary);
    color: var(--primaryFg);
    border: 1px solid var(--primary);
    
    &:hover {
      background: transparent;
      color: var(--primary);
      transform: translateY(-1px);
    }
  }
  
  .btn-secondary {
    background: transparent;
    color: var(--fg);
    border: 1px solid var(--bdr);
    
    &:hover {
      background: var(--primary);
      color: var(--primaryFg);
      border-color: var(--primary);
      transform: translateY(-1px);
    }
  }
`;

const ShowMoreButton = styled.button`
  margin-top: 20px;
  padding: 12px 24px;
  border: 2px solid var(--primary);
  border-radius: 12px;
  background: transparent;
  color: var(--primary);
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  width: 100%;
  
  &:hover {
    background: var(--primary);
    color: var(--primaryFg);
    transform: translateY(-2px);
    box-shadow: 0 6px 20px rgba(176, 141, 87, 0.4);
  }
`;

const initialsFrom = (name = '') => {
  return name.trim().split(/\s+/).map(x => x[0]).slice(0, 2).join('').toUpperCase() || 'M';
};

const fullName = (manager) => {
  return manager?.full_name ||
    [manager?.first_name, manager?.last_name].filter(Boolean).join(' ') ||
    [manager?.first_name_ru, manager?.last_name_ru].filter(Boolean).join(' ') ||
    [manager?.first_name_en, manager?.last_name_en].filter(Boolean).join(' ') ||
    manager?.name ||
    'Менеджер';
};

const position = (manager) => {
  return manager?.position ||
    manager?.position_ru ||
    manager?.position_en ||
    'Эксперт по недвижимости';
};

const telegramLink = (manager) => {
  if (manager?.telegram && /^https?:\/\//i.test(manager.telegram)) {
    return manager.telegram;
  }
  const raw = (manager?.telegram || manager?.phone || '').replace(/\D/g, '');
  return raw ? `https://t.me/+${raw}` : 'https://t.me/+79309729797';
};

export default function DistrictManagers({ managers = [], language = 'ru' }) {
  const [showAll, setShowAll] = React.useState(false);
  
  if (!managers.length) {
    return null;
  }

  // Показываем только первых 2 менеджеров, если не нажата кнопка "Показать все"
  const displayedManagers = showAll ? managers : managers.slice(0, 2);
  const hasMore = managers.length > 2;

  return (
    <div>
      <ManagersWrap>
        {displayedManagers.map((manager, index) => (
          <ManagerCard key={manager.id || index} className="manager-card">
            <Avatar>
              {(manager.avatar_thumb || manager.avatar || manager.photo) ? (
                <img
                  src={normalizeMediaUrl(manager.avatar_thumb || manager.avatar || manager.photo)}
                  alt={String(fullName(manager))}
                />
              ) : (
                <span>{initialsFrom(fullName(manager))}</span>
              )}
            </Avatar>
            
            <ManagerInfo>
              <div className="name">{String(fullName(manager))}</div>
              <div className="position">{String(position(manager))}</div>
              {(manager.phone || manager.email) && (
                <div className="contact">
                  {manager.phone && <div>{String(manager.phone)}</div>}
                  {manager.email && <div>{String(manager.email)}</div>}
                </div>
              )}
            </ManagerInfo>
            
            <Actions>
              <a 
                className="btn btn-primary" 
                href={telegramLink(manager)} 
                target="_blank" 
                rel="noreferrer"
              >
                Telegram
              </a>
              <button className="btn btn-secondary">
                {language === 'en' ? 'Call back' : 'Перезвонить'}
              </button>
            </Actions>
          </ManagerCard>
        ))}
      </ManagersWrap>
      
      {hasMore && !showAll && (
        <ShowMoreButton onClick={() => setShowAll(true)}>
          {language === 'en' ? 'Show all managers' : 'Показать всех менеджеров'}
        </ShowMoreButton>
      )}
    </div>
  );
}